import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';
import { defaultLanguage } from '../../consts';
import { NotFoundGuard } from '../../guards/not-found.guard';
import { getAllRouteData } from '../../lib/get-all-route-data';
import { getFullPath } from '../../lib/get-full-path';
import { PICFLOW_STORYBLOK } from '../../picflow-storyblok';
import { AffiliateService } from '../../services/affiliate.service';
import { RedirectService } from '../../services/redirect.service';
import { mapStoryblokLang } from '../lib/lang-map';

export const storyblokStoryGuard: CanActivateFn = async route => {
  const storyblokService = inject(PICFLOW_STORYBLOK);
  const redirectService = inject(RedirectService);
  const affiliateService = inject(AffiliateService);
  const notFoundGuard = inject(NotFoundGuard);

  const path = (getAllRouteData(route).storyblokPathOverride as string) || getFullPath(route).join('/') || 'home';

  const story = await firstValueFrom(
    storyblokService.getStory(path, route).pipe(map(resp => (resp && resp.story) || (false as const)))
  );

  if (!story) {
    const path = getFullPath(route);
    const first = path[0];
    if (path.length === 1 && first && !first.includes('.')) {
      const affiliate = await affiliateService.getAffiliate(first);
      if (affiliate) return await affiliateService.affiliateRedirect(affiliate);
    }
    return notFoundGuard.canActivate();
  }

  const storyLang = mapStoryblokLang(story.lang);
  if (storyLang !== defaultLanguage) {
    const correctLangPath = story.translated_slugs
      .find(s => s.lang === storyLang)
      ?.path.replace(/home$/, '')
      .replace(/(?<!^)\/$/, '');
    const currentPath = getFullPath(route, false).join('/');
    if (correctLangPath && correctLangPath !== currentPath) {
      return redirectService.redirectTo([storyLang, ...correctLangPath.split('/')], {
        replaceUrl: true,
        queryParamsHandling: 'preserve',
      });
    }
  }

  if (story.content.affiliate) {
    const relationships = await firstValueFrom(storyblokService.resolveRelationships(route));
    const affiliate = relationships.find(r => r.uuid === story.content.affiliate);
    if (route.queryParams.ref !== affiliate.slug) {
      return redirectService.redirectTo(story.full_slug.split('/'), {
        queryParams: {
          ...route.queryParams,
          source: affiliate.content.source,
          medium: affiliate.content.medium,
          campaign: affiliate.content.campaign,
          ref: affiliate.slug,
        },
      });
    }
  }

  if (route.queryParams.ref) {
    const affiliate = await affiliateService.getAffiliate(route.queryParams.ref);
    if (affiliate) {
      await affiliateService.handleAffiliate(affiliate);
    }
  }

  await storyblokService.loadComponents(story);
  if (getFullPath(route, false).pop()?.startsWith('page-')) {
    const blogPostLinkList = story.content.body?.find(c => c.component === 'BlogPostLinkList');
    if (!blogPostLinkList || blogPostLinkList.posts?.length) {
      return redirectService.redirectTo(path.split('/'), { queryParams: route.queryParams });
    }
  }

  return true;
};
